import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  getEquipmentKBDefinition,
  getSimulatedFeedStatus,
  putEquipmentSignalMap,
  suggestEquipmentSignalMap,
} from "@/lib/api/building";
import type { DataPointNature } from "@/lib/api/building";
import {
  equipmentCandidateGroups,
  type RemapCandidateGroup,
} from "@/lib/kb-remap-groups";
import { cardSubtle } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { dataPointLabel } from "@/lib/data-point-labels";
import {
  build_signal_map,
  claimed_target_ids,
  is_draft_source_row_id,
  map_row_target,
  next_draft_source_row_id,
  partition_signal_rows,
  preferred_from_suggestions,
  preferred_source_targets,
  seed_pair_targets,
  source_already_listed,
  targets_from_preferred,
  unmapped_to_draft_rows,
  wired_rows_missing_signaali,
} from "./signaalit-muokkaa-choice";

export type MuokkaaSignalRow = {
  id: string;
  nature: DataPointNature;
  /** Non-null when this id's value is fed from another equipment/zone. */
  connection_source: string | null;
  /** Raw CSV / vendor column when known from csv_signal_schemas. */
  source_signal?: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buildingId: string;
  equipmentId: string;
  equipmentName: string;
  /** Signals currently on the list (mapped identity). */
  signals: MuokkaaSignalRow[];
  labels?: Record<string, string>;
  /**
   * Previously chosen lähdesignaali→Signaali pairs kept when a row was
   * switched off (omitted from the active map). Restored into the Select
   * so off does not wipe the suggested kytkentä.
   */
  preferredSourceTargets?: Record<string, string>;
  /** After Tallenna — preferred pairs include ei kytketty rows that still have a Signaali. */
  onSaved: (preferredSourceTargets: Record<string, string>) => void;
};

const SIGNAL_GROUP_LABEL =
  "px-2 py-1 text-[10px] font-normal uppercase tracking-[0.14em] text-muted-foreground";
const SIGNAL_ITEM =
  "py-1.5 pl-2 pr-8 text-[11px] font-normal text-foreground/90";

const ROW_GRID =
  "grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1.1fr)_2rem] items-center gap-x-4 gap-y-2";

/**
 * Source → target map editor. Taught pairs plus unmapped / typed lähdesignaali
 * rows. Per-row Switch is kytketty / ei kytketty (draft until Tallenna).
 * Off + Tallenna omits receiving from the active map but keeps the chosen
 * Signaali as a preferred pair for the next open. × drops the row entirely.
 */
export function SignaalitMuokkaaDialog({
  open,
  onOpenChange,
  buildingId,
  equipmentId,
  equipmentName,
  signals,
  labels,
  preferredSourceTargets = {},
  onSaved,
}: Props) {
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [draftRows, setDraftRows] = useState<MuokkaaSignalRow[]>([]);
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  /** Row id → kytketty. Record (not Set) so toggles always re-render cleanly. */
  const [wiredById, setWiredById] = useState<Record<string, boolean>>({});
  const [newSourceName, setNewSourceName] = useState("");
  const [candidateGroups, setCandidateGroups] = useState<RemapCandidateGroup[]>(
    [],
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const taughtRows = useMemo(() => {
    const { pairRows: taught } = partition_signal_rows(signals, "all");
    return taught;
  }, [signals]);

  useEffect(() => {
    if (!open) return;
    setRemovedIds(new Set());
    setNewSourceName("");
    setError(null);
    const { pairRows: taught } = partition_signal_rows(signals, "all");
    const initialWired: Record<string, boolean> = {};
    for (const row of taught) initialWired[row.id] = true;

    // Always seed Signaali (taught id / preferred) so off and on both show the ehdotus.
    setTargets(seed_pair_targets(taught, preferredSourceTargets));
    setDraftRows([]);
    setWiredById(initialWired);

    getEquipmentKBDefinition(buildingId, equipmentId)
      .then((def) => setCandidateGroups(equipmentCandidateGroups(def)))
      .catch(() => setCandidateGroups([]));

    // Unmapped CSV headers — lähdesignaali still present, ei kytketty.
    // Preferred + KB matcher ehdotus fill Signaali whether switch is off or on.
    getSimulatedFeedStatus(buildingId, equipmentId)
      .then(async (status) => {
        const unmapped = status?.unmapped_signals ?? [];
        if (unmapped.length === 0) return;
        const { rows } = unmapped_to_draft_rows(unmapped, taught);
        if (rows.length === 0) return;
        const draftMuokkaa = rows.map((row) => ({
          ...row,
          nature: "sensor" as const,
        }));
        setDraftRows(draftMuokkaa);
        setWiredById((prev) => {
          const next = { ...prev };
          for (const row of draftMuokkaa) next[row.id] = false;
          return next;
        });

        const remembered = targets_from_preferred(
          draftMuokkaa,
          preferredSourceTargets,
        );
        setTargets((prev) => ({ ...prev, ...remembered }));

        // Real KB matcher (same as CSV upload) — Finnish BMS names need pattern_hints.
        try {
          const suggestions = await suggestEquipmentSignalMap(
            buildingId,
            equipmentId,
            draftMuokkaa
              .map((row) => row.source_signal?.trim() ?? "")
              .filter(Boolean),
          );
          const fromKb = preferred_from_suggestions(suggestions);
          setTargets((prev) => ({
            ...prev,
            // Remembered / operator picks win over a fresh KB ehdotus.
            ...targets_from_preferred(draftMuokkaa, {
              ...fromKb,
              ...preferredSourceTargets,
            }),
          }));
        } catch {
          /* matcher unavailable — Select stays empty until operator picks */
        }
      })
      .catch(() => {
        /* no simulated connector — CSV-only map has no unmapped header list yet */
      });
    // Snapshot when the dialog opens — not on every signals identity change mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, buildingId, equipmentId]);

  const pairRows = useMemo(() => {
    const keptTaught = taughtRows.filter((row) => !removedIds.has(row.id));
    return [...keptTaught, ...draftRows].sort((a, b) =>
      (a.source_signal ?? "").localeCompare(b.source_signal ?? "", "fi"),
    );
  }, [taughtRows, draftRows, removedIds]);

  const wiredIds = useMemo(() => {
    const ids = new Set<string>();
    for (const row of pairRows) {
      if (wiredById[row.id] !== false) ids.add(row.id);
    }
    return ids;
  }, [pairRows, wiredById]);

  const claimedIds = useMemo(
    () => claimed_target_ids(pairRows, targets, wiredIds),
    [pairRows, targets, wiredIds],
  );

  function setTarget(rowId: string, targetId: string) {
    setTargets((prev) => ({ ...prev, [rowId]: targetId }));
    setError(null);
  }

  function setWired(rowId: string, wired: boolean) {
    if (!wired) {
      // Snapshot the current Signaali so off keeps the choice for later on.
      const row = pairRows.find((r) => r.id === rowId);
      const target = row ? map_row_target(row, targets) : null;
      if (row && target) {
        setTargets((prev) => ({ ...prev, [rowId]: target }));
      }
    }
    setWiredById((prev) => ({ ...prev, [rowId]: wired }));
    setError(null);
  }

  function removeRow(rowId: string) {
    if (is_draft_source_row_id(rowId)) {
      setDraftRows((prev) => prev.filter((row) => row.id !== rowId));
    } else {
      setRemovedIds((prev) => {
        const next = new Set(prev);
        next.add(rowId);
        return next;
      });
    }
    setTargets((prev) => {
      if (!(rowId in prev)) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setWiredById((prev) => {
      if (!(rowId in prev)) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setError(null);
  }

  function addSourceName() {
    const name = newSourceName.trim();
    if (source_already_listed(pairRows, name)) {
      setError(
        name ? "Tämä lähdesignaali on jo listalla" : "Anna lähdesignaalin nimi",
      );
      return;
    }
    const id = next_draft_source_row_id(pairRows.map((row) => row.id));
    setDraftRows((prev) => [
      ...prev,
      {
        id,
        nature: "sensor",
        connection_source: null,
        source_signal: name,
      },
    ]);
    setWiredById((prev) => ({ ...prev, [id]: true }));
    setNewSourceName("");
    setError(null);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      // Only kytketty rows with a Signaali enter the active map — ei kytketty
      // is omitted from receiving but preferred_source_targets keeps the choice.
      const wiredOnly = new Set(
        pairRows
          .filter((row) => wiredById[row.id] !== false)
          .map((row) => row.id),
      );
      const missing = wired_rows_missing_signaali(pairRows, targets, wiredOnly);
      if (missing.length > 0) {
        const names = missing
          .map((row) => row.source_signal?.trim())
          .filter(Boolean)
          .join(", ");
        setError(
          names
            ? `Valitse signaali kytketyille riveille: ${names}`
            : "Valitse signaali kaikille kytketyille riveille",
        );
        return;
      }
      await putEquipmentSignalMap(
        buildingId,
        equipmentId,
        build_signal_map(pairRows, targets, wiredOnly),
      );
      onSaved(preferred_source_targets(pairRows, targets));
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Tallennus epäonnistui");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[min(100%,56rem)] max-w-5xl flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Signaalien kytkennät · {equipmentName}</DialogTitle>
          <DialogDescription>
            Signaali-sarakkeessa näkyy ehdotus myös kun kytkentä on pois —
            liukusäädin vain ottaa vastaanoton käyttöön tai pois. Valitse tai
            vaihda signaalia tarvittaessa. × poistaa rivin. Muutokset tulevat
            voimaan vasta Tallenna-painikkeella.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto py-1">
          <div
            className={cn(
              ROW_GRID,
              "px-1 text-[10px] uppercase tracking-[0.14em] text-muted-foreground",
            )}
          >
            <div>Lähdesignaali</div>
            <div className="text-center normal-case tracking-normal">
              <span className="uppercase tracking-[0.14em]">Kytketty</span>
              <span className="mt-0.5 block text-[9px] font-normal opacity-80">
                pois · päällä
              </span>
            </div>
            <div>Signaali</div>
            <div className="sr-only">Poista</div>
          </div>

          <div className="space-y-1.5">
            {pairRows.map((row) => {
              const source = row.source_signal?.trim() ?? "";
              const isDraft = is_draft_source_row_id(row.id);
              const targetId = map_row_target(row, targets);
              const wired = wiredById[row.id] !== false;
              const missingSignaali = wired && !targetId;
              const selectedMissing =
                Boolean(targetId) &&
                !candidateGroups.some((group) =>
                  group.dataPoints.some((dp) => dp.id === targetId),
                );
              return (
                <div
                  key={row.id}
                  className={cn(
                    cardSubtle,
                    "space-y-1 px-2.5 py-2 text-[11px]",
                  )}
                >
                  <div className={ROW_GRID}>
                    <div
                      className="min-w-0 truncate font-mono text-[10px] text-muted-foreground"
                      title={source}
                    >
                      {source}
                    </div>
                    <div
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <Switch
                        checked={wired}
                        onCheckedChange={(next) => setWired(row.id, next)}
                        aria-label={
                          wired
                            ? `Kytketty: ${source}`
                            : `Ei kytketty: ${source}`
                        }
                      />
                    </div>
                    <div className="min-w-0 space-y-0.5">
                      <Select
                        value={targetId ?? undefined}
                        onValueChange={(v) => setTarget(row.id, v)}
                      >
                        <SelectTrigger
                          className={cn(
                            "h-8 min-w-0 text-[11px]",
                            !wired && "opacity-80",
                            missingSignaali && "border-destructive/60",
                          )}
                          aria-label={`Signaali ${
                            targetId
                              ? dataPointLabel(targetId, labels)
                              : isDraft
                                ? source
                                : dataPointLabel(row.id, labels)
                          }`}
                        >
                          <SelectValue placeholder="Valitse signaali…">
                            {targetId
                              ? dataPointLabel(targetId, labels)
                              : undefined}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {selectedMissing && targetId ? (
                            <SelectItem
                              value={targetId}
                              className={SIGNAL_ITEM}
                            >
                              {dataPointLabel(targetId, labels)}
                            </SelectItem>
                          ) : null}
                          {candidateGroups.map((group) => (
                            <SelectGroup key={group.label ?? "__flat__"}>
                              {group.label && (
                                <SelectLabel className={SIGNAL_GROUP_LABEL}>
                                  {group.label}
                                </SelectLabel>
                              )}
                              {group.dataPoints.map((dp) => {
                                const claimedByOther =
                                  claimedIds.has(dp.id) && targetId !== dp.id;
                                return (
                                  <SelectItem
                                    key={dp.id}
                                    value={dp.id}
                                    disabled={claimedByOther}
                                    className={SIGNAL_ITEM}
                                  >
                                    {dp.name_fi}
                                    {claimedByOther ? " (varattu)" : ""}
                                  </SelectItem>
                                );
                              })}
                            </SelectGroup>
                          ))}
                        </SelectContent>
                      </Select>
                      {!wired && targetId ? (
                        <p className="text-[9px] text-muted-foreground">
                          ehdotus · ei vastaanotossa
                        </p>
                      ) : null}
                      {missingSignaali ? (
                        <p className="text-[9px] text-destructive">
                          Valitse signaali ennen tallennusta
                        </p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRow(row.id)}
                      aria-label={`Poista kytkentä ${source}`}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
            {pairRows.length === 0 && (
              <p className="text-[11px] text-muted-foreground px-1">
                Ei lähdesignaaleja. Lisää nimi alla tai opeta kartta Syötä
                dataa -polulla.
              </p>
            )}
          </div>

          <form
            className="flex items-center gap-2 px-1"
            onSubmit={(event) => {
              event.preventDefault();
              addSourceName();
            }}
          >
            <Input
              value={newSourceName}
              onChange={(event) => setNewSourceName(event.target.value)}
              placeholder="Lähdesignaalin nimi"
              aria-label="Uusi lähdesignaali"
              className="font-mono text-[11px]"
              wrapperClassName="min-w-0 flex-1"
            />
            <button
              type="submit"
              className="shrink-0 rounded-md border border-border bg-surface px-3 py-2 text-xs font-medium hover:border-primary/30"
            >
              Lisää lähdesignaali
            </button>
          </form>

          {error && (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1.5 text-[11px] text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 sm:gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="rounded-md border border-border bg-surface px-3 py-2 text-xs font-medium hover:border-primary/30"
          >
            Peruuta
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Tallennetaan…" : "Tallenna"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
